import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as SearchIcon } from '../../../../assets/icons/search-icon.svg';
import { useAppBranding } from '../../../../config/branding';
import { useAppSelector } from '../../../../redux/hooks';
import CustomDatePicker from '../../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface SessionData {
  id: string;
  _id: string;
  chargingPointSn: string;
  chargingStation: string; // Station ID reference
  transactionStartsAt: string; // ISO date string
  transactionEndsAt: string; // ISO date string
  powerConsumedInKwh: string; // API returns string
  totalAmount: string; // API returns string
  chargingStationDetails: {
    _id: string;
    name: string;
  };
}

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}
interface VehicleProperties {
  vehicle: string;
  type: string;
}

/**
 * SessionDetails component to show charging session history for an end consumer.
 * @returns JSX.Element
 */
function SessionDetails({ vehicle, type }: VehicleProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('endConsumer');
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  const [filteredList, setFilteredList] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [sortData, setSortData] = useState({
    sortField: 'startTime',
    sortBy: 'desc',
  });

  /**
   * Fetch session list from API
   */
  const fetchSessions = useCallback(
    async (userCustomId: string, search?: string) => {
      setLoading(true);
      try {
        const query: any = {};

        if (dateRange.startDate) {
          query.startDate = dayjs(dateRange.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        if (dateRange.endDate) {
          query.endDate = dayjs(dateRange.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        // ✅ ADD SEARCH PARAM
        if (search && search.trim() !== '') {
          query.searchBy = search.trim();
        }
        query.vehicleId = vehicle;
        query.type = type;
        const response = await endConsumerApi.fetchSessionList(userCustomId, query);
        const data = response?.data ?? [];

        setFilteredList(data);
      } catch (error) {
        console.error('Failed to fetch session list:', error);
      } finally {
        setLoading(false);
      }
    },
    [dateRange],
  );

  /**
   * Handles updating the search term state from CustomInput
   * @param name - Field name
   * @param value - Input value (string or number)
   */
  const handleSearchChange = (name: string, value: string | number): void => {
    setSearchTerm(String(value));
  };

  /**
   * Handle date range change
   */
  const handleDateChange = (name: string, value: string) => {
    setDateRange((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle sort
   */
  const handleSortChange = (newSortData: any) => {
    setSortData(newSortData);
  };

  /**
   * Handle open end consumer details
   */
  const handleOpenDeviceDetails = (rowData: SessionData) => {
    navigate(`?deviceId=${rowData.chargingPointSn}`);
    setIsOpen(true);
    setModelDetails({
      title: '',
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${rowData.chargingPointSn}/device-details?searchBy=${rowData.chargingPointSn}`,
    });
  };

  /**
   * Handle open charge session details
   */
  const handleOpenChargeSessionDetails = () => {
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="props" />,
    });
  };

  /**
   * Handle open station details
   */
  const handleOpenStationDetails = (rowData: SessionData) => {
    navigate(`?stationId=${rowData?.chargingStationDetails?._id}`);
    setIsOpen(true);
    setModelDetails({
      title: '',
      content: <StationSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/station/${rowData.chargingStationDetails?._id}/station-dashboard?searchBy=${rowData.chargingStationDetails?.name}`,
    });
  };

  const columns: any[] = [
    {
      label: t('dateTime'),
      accessorKey: 'startTime',
      sortAllow: true,
      /**
       * SessionData
       * @param row
       * @returns
       */
      customData: (currentRow: SessionData) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.transactionStartsAt)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.transactionStartsAt)}</p>
        </div>
      ),
    },
    {
      label: t('station'),
      accessorKey: 'chargingPointSn',
      sortAllow: true,
      /**
       * Rendering station name with hyper link
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: SessionData) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenStationDetails(currentRow)}>
          {currentRow?.chargingStationDetails?.name || ''}
        </p>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn',
      sortAllow: true,
      /**
       * Rendering device name with hyper link
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: SessionData) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow?.chargingPointSn || ''}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed',
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SessionData) => (
        <div className="value-container">
          <p
            role="presentation"
            className="hyper-link value"
            onClick={handleOpenChargeSessionDetails}>
            {ToFixedValue(Number(currentRow?.powerConsumedInKwh))}
          </p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount',
      sortAllow: true,
      /**
       * SessionData
       * @param row
       * @returns
       */
      /**
       * SessionData
       * @param row
       * @returns
       */
      customData: (row: SessionData) => (
        <div className="value-container">
          <p className="amount-text value">{ToFixedValue(row?.totalAmount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const delayDebounce = setTimeout(() => {
      fetchSessions(selectedEndConsumer.userCustomId, searchTerm);
    }, 500); // wait 500ms after typing stops

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, dateRange, selectedEndConsumer, fetchSessions]);

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchSessions(selectedEndConsumer.userCustomId);
    }
  }, [fetchSessions, selectedEndConsumer]);

  return (
    <div className="session-details-wrap-v2">
      <div className="session-details-filters">
        <div className="search-filter">
          <CustomInput
            type="text"
            name="search"
            placeholder={t('searchHere')}
            value={searchTerm}
            onChangeValue={handleSearchChange}
            svgIcon={<SearchIcon />}
          />
        </div>
        <div className="date-filters">
          <CustomDatePicker
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleDateChange}
            placeholder="DD/MM/YYYY"
          />
          <CustomDatePicker
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleDateChange}
            placeholder="DD/MM/YYYY"
          />
        </div>
      </div>

      <div className="session-details-table-wrap">
        <SidePaneModel
          open={isOpen}
          title={modelDetails.title}
          content={modelDetails.content}
          closePanel={handleCloseModal}
          url={modelDetails.url}
        />
        <CustomTable
          header={columns}
          data={filteredList}
          loading={loading}
          serialNo
          version="v1"
          height={window.innerHeight >= 700 ? 320 : 322}
          sortData={sortData}
          handleSortChange={handleSortChange}
          disableHeader
          disablePagination
        />
      </div>
    </div>
  );
}

export default SessionDetails;
